import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { 
  User, 
  Mail, 
  Briefcase, 
  Bell, 
  Shield, 
  Trash2, 
  Download, 
  AlertTriangle,
  Loader2,
  FileDown,
  ShieldAlert
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { apiRequest } from "../lib/utils";
import { toast } from "../components/ToastProvider";

export const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // LGPD states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteMotivo, setDeleteMotivo] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const data = await apiRequest("/lgpd/meus-dados");
      
      // Criar arquivo JSON para download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meus_dados_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Dados exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar dados");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error("Digite sua senha para confirmar");
      return;
    }
    
    setDeleting(true);
    try {
      const result = await apiRequest("/lgpd/solicitar-exclusao", {
        method: "POST",
        body: JSON.stringify({
          senha: deletePassword,
          motivo: deleteMotivo || null
        })
      });
      
      toast.success(result.message);
      
      // Fazer logout e redirecionar
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
    } catch (error) {
      toast.error(error.message || "Erro ao excluir conta");
    } finally {
      setDeleting(false);
    }
  };

  const isAdmin = user?.is_admin || false;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl animate-slide-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Configurações
          </h1>
          <p className="text-slate-500 mt-1">Gerencie suas preferencias e informações</p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Perfil
            </CardTitle>
            <CardDescription>Suas informações pessoais</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="nome"
                    value={user?.nome || ""}
                    className="pl-10"
                    disabled
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    value={user?.email || ""}
                    className="pl-10"
                    disabled
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="cargo"
                    value={user?.cargo || "-"}
                    className="pl-10"
                    disabled
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notificações
            </CardTitle>
            <CardDescription>Configure alertas por email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Alertas de vencimento</p>
                  <p className="text-sm text-slate-500">Receba emails 30 e 15 dias antes do vencimento</p>
                </div>
                <span className="text-sm text-slate-400">Em breve</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Resumo semanal</p>
                  <p className="text-sm text-slate-500">Receba um resumo dos eventos da semana</p>
                </div>
                <span className="text-sm text-slate-400">Em breve</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Seguranca
            </CardTitle>
            <CardDescription>Configurações de acesso</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Alterar Senha</Label>
              <p className="text-sm text-slate-500 mb-2">
                Para alterar sua senha, utilize a opção "Esqueci minha senha" na tela de login.
              </p>
              <Button variant="outline" onClick={() => { logout(); navigate("/esqueci-senha"); }}>
                Alterar senha
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* LGPD - Privacidade e Dados */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <ShieldAlert className="w-5 h-5" />
              Privacidade e Dados (LGPD)
            </CardTitle>
            <CardDescription>
              Gerencie seus dados conforme a Lei Geral de Proteção de Dados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Exportar Dados */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileDown className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">Exportar Meus Dados</h4>
                  <p className="text-sm text-slate-500 mt-1 mb-3">
                    Baixe uma copia de todos os seus dados armazenados no sistema (Art. 18, LGPD).
                    O arquivo será gerado em formato JSON.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={handleExportData}
                    disabled={exporting}
                    className="flex items-center gap-2"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {exporting ? "Exportando..." : "Exportar Dados"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Excluir Conta */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-red-900">Excluir Minha Conta</h4>
                  <p className="text-sm text-red-700 mt-1 mb-3">
                    {isAdmin ? (
                      <>
                        <strong>Atenção:</strong> Como administrador, esta ação excluira permanentemente 
                        a farmácia e TODOS os dados relacionados (documentos, funcionarios, verificacoes, 
                        usuarios convidados, etc.). Esta ação NAO pode ser desfeita.
                      </>
                    ) : (
                      <>
                        Ao excluir sua conta, seus dados de acesso serão removidos permanentemente. 
                        Esta ação NAO pode ser desfeita.
                      </>
                    )}
                  </p>
                  <Button 
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isAdmin ? "Excluir Farmácia e Dados" : "Excluir Minha Conta"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>
                Conforme a Resolução
              </h3>
              <p className="text-sm text-slate-500 mb-2">
                Sistema de Gestão Documental para Farmácias de Manipulação
              </p>
              <p className="text-xs text-slate-400">
                Versão 1.0.0 • © 2025
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>
                {isAdmin ? (
                  "Você esta prestes a excluir permanentemente a farmácia e TODOS os dados relacionados. Esta ação e IRREVERSIVEL."
                ) : (
                  "Você esta prestes a excluir sua conta permanentemente. Esta ação e IRREVERSIVEL."
                )}
              </p>
              
              <div className="space-y-2 pt-2">
                <Label htmlFor="delete-password">Digite sua senha para confirmar:</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Sua senha atual"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-motivo">Motivo da exclusão (opcional):</Label>
                <Textarea
                  id="delete-motivo"
                  value={deleteMotivo}
                  onChange={(e) => setDeleteMotivo(e.target.value)}
                  placeholder="Conte-nos por que esta excluindo..."
                  rows={2}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeletePassword("");
              setDeleteMotivo("");
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting || !deletePassword}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Permanentemente
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default Settings;
